// The web side of POST /api/v1/calls/{id}/coach/feedback (docs/design/CALL-POP-KRISP-BAR.md lane 6): the request, the
// target ids for a tip, an answer and a summary, and CoachFeedbackVotes, this window's record of the rep's votes, which
// the call window turns into TipFeedback's voted and onVote. A click shows at once; a failed send puts the vote the
// server last accepted back. Sends for one target go one at a time and only the latest click is sent, so the server
// ends with what the rep clicked last. The server keeps one vote per user per call per target.
import type { CoachFeedback, CoachFeedbackTarget, CoachFeedbackVote, CoachResponse } from '@bcio-voice/contracts';
import { useCallback, useSyncExternalStore } from 'react';
import type { ApiClient } from '../../lib/api.ts';

export const coachFeedbackPath = (callId: string): string => `/calls/${encodeURIComponent(callId)}/coach/feedback`;
/** The API's rule for target_id: printable ASCII without spaces, 1 to 200 characters. */
export const FEEDBACK_TARGET_ID = /^[\x21-\x7e]{1,200}$/;
export const FEEDBACK_NOTE_MAX = 300;

/** 32-bit FNV-1a as 8 hex digits: a short, stable name for some text, never sent as the text itself. */
export function shortHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}

const idOf = (prefix: string, raw: string): string => {
  const id = `${prefix}:${raw}`;
  return FEEDBACK_TARGET_ID.test(id) ? id : `${prefix}:${shortHash(raw)}`;
};

/** A tip is the coach answer computed at one moment: its time and a hash of its lines. Null while there is no tip. */
export function tipFeedbackTargetId(tip: Pick<CoachResponse, 'computed_at' | 'lines'> | null | undefined): string | null {
  if (!tip?.computed_at || !tip.lines.length) return null;
  return idOf('tip', `${tip.computed_at}:${shortHash(tip.lines.join('\n'))}`);
}
/** An answer in the question thread, by its entry id (CoachQaEntry.id). */
export function answerFeedbackTargetId(entryId: string | null | undefined): string | null {
  return entryId ? idOf('answer', entryId) : null;
}
/** A wrap-up, by its call and version, so a rewritten summary is rated afresh. */
export function summaryFeedbackTargetId(summary: { call_id: string; version: number } | null | undefined): string | null {
  return summary ? idOf('summary', `${summary.call_id}:${summary.version}`) : null;
}

/** The request body, or null when the target id is not one the API accepts. A blank note is left out. */
export function coachFeedbackBody(target: CoachFeedbackTarget, targetId: string, vote: CoachFeedbackVote, note?: string | null): CoachFeedback | null {
  if (!FEEDBACK_TARGET_ID.test(targetId)) return null;
  const text = (note ?? '').replace(/[\x00-\x1f\x7f-\x9f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, FEEDBACK_NOTE_MAX);
  return { target, target_id: targetId, vote, ...(text ? { note: text } : {}) };
}

/** One vote to the API. Resolves on 204; rejects with the ApiRequestError otherwise. */
export async function sendCoachFeedback(api: Pick<ApiClient, 'send'>, callId: string, body: CoachFeedback, signal?: AbortSignal): Promise<void> {
  await api.send<void>('POST', coachFeedbackPath(callId), body, signal ? { signal } : {});
}

interface VoteEntry { shown: CoachFeedbackVote | null; wanted: CoachFeedbackVote | null; confirmed: CoachFeedbackVote | null; chain: Promise<void> }

/** This window's votes, keyed by call, target and target id. The call window keeps one and reads voted() per target. */
export class CoachFeedbackVotes {
  private readonly entries = new Map<string, VoteEntry>();
  private readonly listeners = new Set<() => void>();
  private revision = 0;
  constructor(private readonly api: Pick<ApiClient, 'send'>, private readonly options: { onError?: (error: unknown) => void; limit?: number } = {}) {}

  private key(callId: string, target: CoachFeedbackTarget, targetId: string): string { return `${callId}\n${target}\n${targetId}`; }
  private notify(): void { this.revision += 1; for (const l of [...this.listeners]) l(); }

  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  /** Changes whenever any shown vote changes (a useSyncExternalStore snapshot). */
  snapshot = (): number => this.revision;

  /** The vote shown for this target: the rep's latest click, or the server's last accepted vote after a failure. */
  voted(callId: string, target: CoachFeedbackTarget, targetId: string | null): CoachFeedbackVote | null {
    return targetId ? this.entries.get(this.key(callId, target, targetId))?.shown ?? null : null;
  }

  /** Shows the vote at once and sends it. Clicking the vote the server already has sends nothing. */
  vote(callId: string, target: CoachFeedbackTarget, targetId: string, vote: CoachFeedbackVote, note?: string | null): Promise<void> {
    const body = coachFeedbackBody(target, targetId, vote, note);
    if (!body) return Promise.reject(new Error('invalid_feedback_target'));
    const key = this.key(callId, target, targetId);
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { shown: null, wanted: null, confirmed: null, chain: Promise.resolve() };
      this.entries.set(key, entry);
      const limit = this.options.limit ?? 500;
      while (this.entries.size > limit) { const oldest = this.entries.keys().next().value; if (oldest === undefined || oldest === key) break; this.entries.delete(oldest); }
    }
    const e = entry;
    if (e.wanted === vote && e.confirmed === vote) return Promise.resolve();
    e.wanted = vote;
    if (e.shown !== vote) { e.shown = vote; this.notify(); }
    const run = e.chain.then(async () => {
      // A later click replaced this one, or the server already has it.
      if (e.wanted !== vote || e.confirmed === vote) return;
      try {
        await sendCoachFeedback(this.api, callId, body);
        e.confirmed = vote;
      } catch (error) {
        if (e.wanted === vote) { e.wanted = e.confirmed; if (e.shown !== e.confirmed) { e.shown = e.confirmed; this.notify(); } }
        this.options.onError?.(error);
        throw error;
      }
    });
    e.chain = run.catch(() => undefined);
    return run;
  }

  /** Forgets one call's votes (the window moved to another call) or all of them. */
  clear(callId?: string): void {
    const before = this.entries.size;
    for (const key of [...this.entries.keys()]) if (callId === undefined || key.startsWith(`${callId}\n`)) this.entries.delete(key);
    if (this.entries.size !== before) this.notify();
  }
}

/** TipFeedback's voted and onVote for one target: `<TipFeedback target="tip" targetId={id} {...useCoachVote(votes, callId, 'tip', id)} />`.
 * A failed send is reported through CoachFeedbackVotes' onError, never thrown into React. */
export function useCoachVote(votes: CoachFeedbackVotes, callId: string | null, target: CoachFeedbackTarget, targetId: string | null): { voted: CoachFeedbackVote | null; onVote: (vote: CoachFeedbackVote) => void } {
  useSyncExternalStore(votes.subscribe, votes.snapshot, votes.snapshot);
  const voted = callId ? votes.voted(callId, target, targetId) : null;
  const onVote = useCallback((vote: CoachFeedbackVote) => {
    if (callId && targetId) void votes.vote(callId, target, targetId, vote).catch(() => undefined);
  }, [votes, callId, target, targetId]);
  return { voted, onVote };
}
